import type { ReactNode } from 'react';
import { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import type { Project } from '@gql/graphql';
import { OrderDirection } from '@gql/graphql';
import {
    GET_ALL_PROJECTS,
    GET_PROJECT_TAGS_ME,
    GET_PROJECTS_TASKS_SUMMARY,
    GET_PROJECT_SEARCH_FILTERS,
} from '@api/queries/projects';
import { useInfoCard } from '@components/InfoCard';
import { useDebounce } from '@hooks/useDebounce';
import { useInitialLoading } from '@hooks/useInitialLoading';
import { DisplayMode } from '@models/display-mode';
import type { Maybe } from '@models/Maybe';
import { flatMapEdges } from '@utils/flatMapEdges';
import type { ProjectContextValues } from '../models/projectsContext';
import { buildProjectTaskSummaryMap } from '../utils/project-task-summary';
import { mapQueryResultToOptions } from '../utils/mapQueryResultToStringOption';
import {
    buildProjectsWhere,
    DEFAULT_PROJECT_FILTERS,
    hasActiveProjectFilters,
    sortStringOptions,
    type ProjectFiltersState,
} from '../utils/projectSearch';

const ProjectsContext = createContext<ProjectContextValues>({
    pageInitialLoading: false,
    projects: {
        loading: false,
        data: [],
        isSearchingLoading: false,
        error: undefined,
    },
    projectCompleteness: {
        byProjectId: {},
        error: undefined,
    },
    selection: { get: undefined, set: () => null },
    displayMode: { get: DisplayMode.Cards, set: () => null },
    filters: {
        state: { get: DEFAULT_PROJECT_FILTERS, set: () => null },
        hasActive: false,
        clear: () => null,
        statusOptions: [],
        typeOptions: [],
        tagOptions: [],
        optionsError: undefined,
        tagOptionsError: undefined,
    },
    expandInfoCard: () => null,
});

interface ProjectsProviderProps {
    children: ReactNode;
}

export function ProjectsProvider({
    children,
}: ProjectsProviderProps): ReactNode {
    const { setOpen } = useInfoCard();
    const [filtersState, setFiltersState] = useState<ProjectFiltersState>(
        DEFAULT_PROJECT_FILTERS,
    );
    const [selected, setSelected] = useState<Project | undefined>(undefined);
    const [displayModeState, setDisplayModeState] = useState<DisplayMode>(
        DisplayMode.Cards,
    );

    const debouncedSearch = useDebounce(filtersState.search, 250);

    const queryFilters = useMemo(
        () => ({ ...filtersState, search: debouncedSearch }),
        [debouncedSearch, filtersState],
    );
    const where = useMemo(
        () => buildProjectsWhere(queryFilters),
        [queryFilters],
    );

    const { loading, error, data } = useQuery(GET_ALL_PROJECTS, {
        variables: { direction: OrderDirection.Asc, where },
        fetchPolicy: 'cache-and-network',
        skip: displayModeState !== DisplayMode.Cards,
    });
    const {
        loading: filtersLoading,
        error: filtersError,
        data: filterOptionsData,
    } = useQuery(GET_PROJECT_SEARCH_FILTERS, {
        fetchPolicy: 'cache-first',
    });
    const {
        loading: tagOptionsLoading,
        error: tagOptionsError,
        data: tagOptionsData,
    } = useQuery(GET_PROJECT_TAGS_ME, {
        fetchPolicy: 'cache-and-network',
    });

    const projects = useMemo(
        () => (flatMapEdges(data?.projects) as unknown as Project[]) ?? [],
        [data],
    );
    const isColdLoading =
        displayModeState === DisplayMode.Cards &&
        ((loading && !data) ||
            (filtersLoading && !filterOptionsData) ||
            (tagOptionsLoading && !tagOptionsData));
    const previousDisplayModeRef = useRef(displayModeState);
    const cardsViewResetKeyRef = useRef(0);
    if (
        displayModeState === DisplayMode.Cards &&
        previousDisplayModeRef.current !== DisplayMode.Cards
    ) {
        cardsViewResetKeyRef.current += 1;
    }
    previousDisplayModeRef.current = displayModeState;

    const isInitialMountRef = useRef(true);

    useEffect(() => {
        if (!loading) {
            isInitialMountRef.current = false;
        }
    }, [loading]);

    const projectIds = useMemo(
        () => projects.map((project) => project.id),
        [projects],
    );
    const {
        loading: taskSummaryQueryLoading,
        data: taskSummaryData,
        error: taskSummaryError,
    } = useQuery(GET_PROJECTS_TASKS_SUMMARY, {
        variables: { projectIDs: projectIds },
        skip: displayModeState !== DisplayMode.Cards || projectIds.length === 0,
        fetchPolicy: 'cache-and-network',
    });
    const taskSummaryLoading =
        displayModeState === DisplayMode.Cards &&
        projectIds.length > 0 &&
        taskSummaryQueryLoading &&
        !taskSummaryData;
    const cardsInitialLoading = useInitialLoading(
        Boolean(isColdLoading) || taskSummaryLoading,
        cardsViewResetKeyRef.current,
    );
    const projectCompleteness = useMemo(
        () => ({
            byProjectId: buildProjectTaskSummaryMap(
                projectIds,
                flatMapEdges(taskSummaryData?.tasks),
            ),
            error: taskSummaryError,
        }),
        [projectIds, taskSummaryData, taskSummaryError],
    );
    const statusOptions = useMemo(
        () =>
            sortStringOptions(
                mapQueryResultToOptions(filterOptionsData?.projectStatuses),
            ),
        [filterOptionsData?.projectStatuses],
    );
    const typeOptions = useMemo(
        () =>
            sortStringOptions(
                mapQueryResultToOptions(filterOptionsData?.projectTypes),
            ),
        [filterOptionsData?.projectTypes],
    );
    const tagOptions = useMemo(
        () =>
            sortStringOptions(
                tagOptionsData?.projectTagsMe.map((tagName) => ({
                    value: tagName,
                    label: tagName,
                })) ?? [],
            ),
        [tagOptionsData?.projectTagsMe],
    );
    const hasActiveFilters = useMemo(
        () => hasActiveProjectFilters(filtersState),
        [filtersState],
    );
    const isFilterOrSearchActive =
        filtersState.search !== '' || hasActiveFilters;
    const isSearchingLoading =
        !cardsInitialLoading &&
        (!isInitialMountRef.current || isFilterOrSearchActive) &&
        (loading || filtersState.search !== debouncedSearch);

    useEffect(() => {
        if (displayModeState !== DisplayMode.Cards || !selected) {
            return;
        }

        const freshSelected = projects.find(
            (project) => project.id === selected.id,
        );

        if (freshSelected !== selected) {
            setSelected(freshSelected);
        }
    }, [displayModeState, projects, selected]);

    return (
        <ProjectsContext.Provider
            value={{
                pageInitialLoading: cardsInitialLoading,
                projects: {
                    loading,
                    data: projects,
                    isSearchingLoading,
                    error,
                },
                projectCompleteness,
                selection: {
                    get: selected,
                    set: (value: Maybe<Project>) => {
                        setSelected(value ?? undefined);
                    },
                },
                displayMode: {
                    get: displayModeState,
                    set: setDisplayModeState,
                },
                filters: {
                    state: { get: filtersState, set: setFiltersState },
                    hasActive: hasActiveFilters,
                    clear: () => {
                        setFiltersState(DEFAULT_PROJECT_FILTERS);
                    },
                    statusOptions,
                    typeOptions,
                    tagOptions,
                    optionsError: filtersError,
                    tagOptionsError,
                },
                expandInfoCard: () => {
                    setOpen(true);
                },
            }}
        >
            {children}
        </ProjectsContext.Provider>
    );
}

export default ProjectsContext;
